/**
 * Permission and Capability Checking Utilities
 * 
 * Provides flexible, extensible methods to check user permissions
 * based on roles and capabilities.
 */

import { ROLE_DEFINITIONS } from '@config/roles';

/**
 * Get capabilities for a user
 * @param {Object} user - User object
 * @returns {Array} Array of capability strings
 */
export function getUserCapabilities(user) {
  if (!user || !user.role) {
    return [];
  }

  const roleDef = ROLE_DEFINITIONS[user.role];
  if (!roleDef) {
    return [];
  }

  return roleDef.capabilities || [];
}

/**
 * Check if user has a specific capability
 * @param {Object} user - User object
 * @param {string} capability - Capability to check
 * @returns {boolean}
 */
export function hasCapability(user, capability) {
  if (!user) return false;
  const capabilities = getUserCapabilities(user);
  return capabilities.includes(capability);
}

/**
 * Check if user has ANY of the specified capabilities
 * @param {Object} user - User object
 * @param {Array} capabilities - Array of capability strings
 * @returns {boolean}
 */
export function hasAnyCapability(user, capabilities) {
  if (!user || !Array.isArray(capabilities)) return false;
  return capabilities.some(cap => hasCapability(user, cap));
}

/**
 * Check if user has ALL of the specified capabilities
 * @param {Object} user - User object
 * @param {Array} capabilities - Array of capability strings
 * @returns {boolean}
 */
export function hasAllCapabilities(user, capabilities) {
  if (!user || !Array.isArray(capabilities)) return false;
  return capabilities.every(cap => hasCapability(user, cap));
}

/**
 * Check if user is admin
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function isAdmin(user) {
  return user?.role === 'admin';
}

/**
 * Check if user is course manager
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function isCourseManager(user) {
  return user?.role === 'course_manager';
}

/**
 * Check if user is student
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function isStudent(user) {
  return user?.role === 'student';
}

/**
 * Check if user has administrative privileges
 * (admin or course manager)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function hasAdminPrivileges(user) {
  return isAdmin(user) || isCourseManager(user);
}

/**
 * Check if user can manage a specific course
 * @param {Object} user - User object
 * @param {string} courseId - Course ID to check
 * @returns {boolean}
 */
export function canManageCourse(user, courseId) {
  if (!user) return false;

  // Admin can manage all courses
  if (isAdmin(user)) {
    return true;
  }

  // Course manager can manage assigned courses
  if (isCourseManager(user)) {
    return (user.managedCourses || []).includes(courseId);
  }

  return false;
}

/**
 * Check if user can view a specific course
 * @param {Object} user - User object
 * @param {string} courseId - Course ID to check
 * @returns {boolean}
 */
export function canViewCourse(user, courseId) {
  if (!user) return false;

  // Admin can view all courses
  if (isAdmin(user)) {
    return true;
  }

  // Course manager can view assigned courses
  if (isCourseManager(user)) {
    return (user.managedCourses || []).includes(courseId);
  }

  // Student can view enrolled courses
  if (isStudent(user)) {
    return (user.enrolledCourses || []).includes(courseId);
  }

  return false;
}

/**
 * Get courses a user can view
 * @param {Object} user - User object
 * @param {Array} allCourses - All available courses
 * @returns {Array} Filtered courses user can view
 */
export function getViewableCourses(user, allCourses = []) {
  if (!user) return [];

  // Admin can view all
  if (isAdmin(user)) {
    return allCourses;
  }

  // Course manager can view managed courses
  if (isCourseManager(user)) {
    const managed = user.managedCourses || [];
    return allCourses.filter(c => managed.includes(c.id));
  }

  // Student can view enrolled courses
  if (isStudent(user)) {
    const enrolled = user.enrolledCourses || [];
    return allCourses.filter(c => enrolled.includes(c.id));
  }

  return [];
}

/**
 * Get courses a user can manage
 * @param {Object} user - User object
 * @param {Array} allCourses - All available courses
 * @returns {Array} Filtered courses user can manage
 */
export function getManagedCourses(user, allCourses = []) {
  if (!user) return [];

  // Admin can manage all
  if (isAdmin(user)) {
    return allCourses;
  }

  // Course manager can manage assigned courses
  if (isCourseManager(user)) {
    const managed = user.managedCourses || [];
    return allCourses.filter(c => managed.includes(c.id));
  }

  return [];
}

/**
 * Check if user can view all users
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canViewAllUsers(user) {
  return hasCapability(user, 'view_all_users');
}

/**
 * Check if user can manage users
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canManageUsers(user) {
  return hasCapability(user, 'manage_all_users');
}

/**
 * Check if user can access admin panel
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canAccessAdminPanel(user) {
  return hasCapability(user, 'view_admin_panel');
}
